import React, {useEffect, useState} from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import AssetListScreen from './assetList';
import AssetDetailsScreen from './assetDetails';
import { Box } from '@mui/system';
import AssetTabTitle from '../../components/AssetTabTitle';
import { ListRounded } from '@mui-symbols-material/w300';
import {useTranslation} from "react-i18next";
import AutoScrollTabs from "../../components/AutoScrollTabs";
import { useKeycloak } from '@react-keycloak/web';
import DashboardPreviewScreen from "../dashboardPreview/dashboardPreviewScreen";

const LEGACY_STORAGE_KEY = 'openAssetTabs';
// Reserved path segment for the env-guarded dashboard rework preview — never a real asset tab.
const PREVIEW_SEGMENT = 'dashboardPreview';

const AssetRoutes = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { keycloak } = useKeycloak();

    const userId = keycloak.tokenParsed?.sub;
    const legacyUserScopedKey = userId ? `openAssetTabs_${userId}` : null;
    const storageKey = userId ? `openAssetLocationTabs_${userId}` : null;

    const match = location.pathname.match(/^\/assets\/([^/]+)/);
    const activeTab = match?.[1] ?? 'list';

    const [openTabs, setOpenTabs] = useState<string[]>([]);
    const [tabsLoaded, setTabsLoaded] = useState(false);

    useEffect(() => {
        // Remove legacy keys (shared key + previous assetId-based user-scoped key).
        // The new key tracks asset location ids, which aren't compatible with stored asset ids.
        localStorage.removeItem(LEGACY_STORAGE_KEY);
        if (legacyUserScopedKey) {
            localStorage.removeItem(legacyUserScopedKey);
        }

        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                try {
                    setOpenTabs(JSON.parse(stored));
                } catch (e) {
                    console.warn('Could not parse localStorage tabs');
                }
            }
        }
        setTabsLoaded(true);
    }, []);

    useEffect(() => {
        if (tabsLoaded && storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(openTabs));
        }
    }, [openTabs, tabsLoaded]);

    useEffect(() => {
        if (!tabsLoaded) return;

        if (activeTab !== 'list' && activeTab !== PREVIEW_SEGMENT && !openTabs.includes(activeTab)) {
            setOpenTabs(prev => [...prev, activeTab]);
        }
    }, [activeTab, tabsLoaded]);

    // Atomic tab swap fired by event-create flows that relocate an asset
    // (e.g. ASSET_REMOVE / ASSET_EXCHANGE produce a new assetLocation id).
    useEffect(() => {
        const handler = (event: Event) => {
            const { from, to } = (event as CustomEvent<{ from: string; to: string }>).detail ?? {};
            if (!to || from === to) return;
            setOpenTabs(prev => {
                const without = prev.filter(id => id !== from);
                return without.includes(to) ? without : [...without, to];
            });
            navigate(`/assets/${to}/details`);
        };
        window.addEventListener('asset-tab-switch', handler);
        return () => window.removeEventListener('asset-tab-switch', handler);
    }, [navigate]);

    const handleTabChange = (event: React.SyntheticEvent | null, value: number | string | null) => {
        if (value === 'list') {
            navigate('/assets');
        } else {
            navigate(`/assets/${value}/details`);
        }
    };

    const handleTabClose = (assetLocationId: string) => {
        const updatedTabs = openTabs.filter(id => id !== assetLocationId);
        setOpenTabs(updatedTabs);

        if (activeTab === assetLocationId) {
            const fallback = updatedTabs.length > 0 ? updatedTabs[updatedTabs.length - 1] : 'list';
            navigate(fallback === 'list' ? '/assets' : `/assets/${fallback}/details`);
        }
    };

    return (
        <Box sx={{ padding: '8px 12px', maxWidth: '100%' }} flexDirection="column" display={'flex'} flexGrow={1}>
            <AutoScrollTabs
                value={activeTab}
                onChange={handleTabChange}
                tabs={[
                    { label: t('List'), value: 'list', icon: <ListRounded />, sticky: true },
                    ...openTabs.map(id => ({ label: <AssetTabTitle assetLocationId={id} onCloseTab={handleTabClose} />, value: id }))
                ]}
                onCloseAll={() => {
                    setOpenTabs([])
                    navigate('/assets')
                }}
                onCloseOthers={() => {
                    const filteredTabs = openTabs.filter(id => id === activeTab);
                    setOpenTabs(filteredTabs);
                }}
            />

            <Routes>
                <Route
                    path="/"
                    element={<AssetListScreen onOpenAsset={(id) => navigate(`/assets/${id}/details`)} />}
                />
                {process.env.REACT_APP_ENABLE_DASHBOARD_PREVIEW === 'true' && (
                    <Route path={`${PREVIEW_SEGMENT}/*`} element={<DashboardPreviewScreen />} />
                )}
                <Route path="/:assetLocationId/*" element={<AssetDetailsScreen />} />
            </Routes>
        </Box>
    );
};

export default AssetRoutes;
